import { apiClient, ensureCsrfCookie } from '../../api/client';

/**
 * Техподдержка через Семёна (`s737`). Бэкенд: /api/admin/support/*.
 * Сотрудник: сообщить о проблеме, свои сообщения, ответ на вопрос, «заработало».
 * Владелец (право support.owner): раздел «Техподдержка», решения, выключатели.
 * Живое — опросом, не потоком (закон php-fpm пяти процессов).
 */

export type IncidentStatus =
  | 'new' | 'triage' | 'in_work' | 'needs_reply' | 'needs_decision' | 'verifying'
  | 'released' | 'fixed' | 'confirmed' | 'obstacle' | 'declined' | 'closed';

export type IncidentSource = 'auto' | 'ui' | 'telegram';
export type IncidentKind = 'defect' | 'wish' | 'question' | 'outside' | null;
export type DecisionKind = 'release' | 'proposal' | 'outside_answer' | 'outside_action';

export interface IncidentFacts {
  received_at: string | null;
  delivered_at: string | null;
  started_at: string | null;
  awaiting_reply_at: string | null;
  awaiting_decision_at: string | null;
  released_at: string | null;
  checks_passed_at: string | null;
  human_confirmed_at: string | null;
}

/** Обращение, как его видит автор. Поля причины и исправления приходят только владельцу. */
export interface IncidentCard {
  id: number;
  title: string;
  summary: string | null;
  status: IncidentStatus;
  kind: IncidentKind;
  source: IncidentSource;
  area: string | null;
  screen: string | null;
  result: string | null;
  occurrences: number;
  reopened_count: number;
  author_user_id: number | null;
  author_role: string | null;
  facts: IncidentFacts;
  created_at: string | null;
  mode?: string | null;
  cause?: string | null;
  plan?: string | null;
  executor?: string | null;
  fix_ref?: string | null;
  fix_version?: string | null;
  context?: Record<string, unknown> | null;
}

export interface MyIncident extends IncidentCard {
  question: { text: string | null; at: string | null } | null;
}

export interface MyResponse {
  incidents: MyIncident[];
  telegram: { bound: boolean; bot: string };
}

export interface IncidentEvent {
  id: number;
  kind: string;
  who: string;
  label: string | null;
  text: string | null;
  at: string | null;
  payload: Record<string, unknown> | null;
}

export interface DecisionRow {
  id: number;
  kind: DecisionKind;
  status?: 'pending' | 'approved' | 'rejected' | 'superseded' | 'consumed';
  subject: Record<string, unknown> | null;
  fix_version: string | null;
  delivered_at: string | null;
  decided_via?: string | null;
  decided_at?: string | null;
}

export interface OwnerAction { action: 'retry' | 'close' | 'take' | 'decline'; label: string; confirm: string | null }

export interface IncidentDetail {
  actions?: OwnerAction[];
  incident: IncidentCard;
  events: IncidentEvent[];
  decisions: DecisionRow[];
}

export interface OwnerRow {
  actions?: OwnerAction[];
  id: number;
  title: string;
  /** Суть простыми словами — есть у сбоев, которые система заметила сама. */
  human: string | null;
  summary: string | null;
  status: IncidentStatus;
  kind: IncidentKind;
  mode: string | null;
  source: IncidentSource;
  area: string | null;
  author: string | null;
  author_id: number | null;
  occurrences: number;
  reopened_count: number;
  result: string | null;
  received_at: string | null;
  awaiting_decision_at: string | null;
  /** Имя серверной сессии, которая сама выпустила исправление; null — выпуск конвейером. */
  release_session: string | null;
  /** Уточнение, которого ждут от автора: текст вопроса и с какого времени. */
  question: string | null;
  awaiting_reply_at: string | null;
  last_seen_at: string | null;
  released_at: string | null;
  checks_passed_at: string | null;
  human_confirmed_at: string | null;
  decisions: DecisionRow[];
}

export type Section = 'decision' | 'work' | 'proposals' | 'done';

export interface SectionCounts { decision: number; work: number; proposals: number; done: number }

export interface OwnerList {
  counts: SectionCounts;
  authors: { id: number; name: string }[];
  areas: string[];
  incidents: OwnerRow[];
}

export interface OwnerFilters {
  section: Section;
  status?: string;
  source?: string;
  author?: string;
  area?: string;
}

export type SwitchName = 'intake' | 'ai' | 'autopublish';

export interface SwitchState { enabled: boolean; reason: string | null; changed_at: string | null }

export interface Overview {
  switches: Record<SwitchName, SwitchState>;
  health: { ok: boolean; parts: { name: string; ok: boolean; detail: string }[] };
  bot: string;
}

export interface ReportPayload {
  text: string;
  request_key: string;
  screen: string;
  app_version: string | null;
  client_time: string;
  passport: Record<string, unknown> | null;
  marked: { x: number; y: number; w: number; h: number } | null;
  snapshot: string | null;
}

export interface ReportResult { accepted: boolean; incident_id: number | null; outcome: string | null }

/** Ручки техподдержки живут под /api/admin/*, а не /api/v1/* — база без версии. */
function rootApi(): string {
  const base = (apiClient.defaults.baseURL ?? 'https://api.opsmind.ru/api/v1').replace(/\/$/, '');
  return base.replace(/\/api\/v1$/, '/api');
}

async function get<T>(path: string, params?: Record<string, string>): Promise<T> {
  const { data } = await apiClient.get(`/admin/support/${path}`, { baseURL: rootApi(), params });
  return data as T;
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(`/admin/support/${path}`, body ?? {}, { baseURL: rootApi() });
  return data as T;
}

export const sendReport = (payload: ReportPayload) => post<ReportResult>('reports', payload);
export const fetchMine = () => get<MyResponse>('my');
export const fetchIncident = (id: number) => get<IncidentDetail>(`incidents/${id}`);
export const replyIncident = (id: number, text: string) => post<{ outcome: string }>(`incidents/${id}/reply`, { text });
export const confirmIncident = (id: number) => post<{ status: IncidentStatus }>(`incidents/${id}/confirm`);
export const issueBindCode = () => post<{ code: string; minutes: number; bot: string }>('bind-code');

export function fetchOwnerList(filters: OwnerFilters): Promise<OwnerList> {
  const params: Record<string, string> = { section: filters.section };
  (['status', 'source', 'author', 'area'] as const).forEach((key) => {
    const value = filters[key];
    if (value) params[key] = value;
  });
  return get<OwnerList>('incidents', params);
}

export const fetchCounts = () => get<{ counts: SectionCounts }>('counts');
export const fetchOverview = () => get<Overview>('overview');

export function decide(decision: DecisionRow, approve: boolean, note: string) {
  return post<{ id: number; status: string }>(`decisions/${decision.id}/decide`, {
    approve,
    fix_version: decision.fix_version,
    note: note.trim() === '' ? null : note.trim(),
  });
}

export const setSwitch = (name: SwitchName, enabled: boolean) =>
  post<{ switches: Record<SwitchName, SwitchState> }>(`switches/${name}`, { enabled });

export const runOwnerAction = (id: number, action: OwnerAction["action"]) => post<{ id: number; status: IncidentStatus }>(`incidents/${id}/action`, { action });
